import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { createColumnHelper, type ColumnDef, type SortingState } from "@tanstack/react-table";
import { type components } from "@njbsoft/types";
import { useCatalogDetectorMeters } from "@/hooks/use-catalog-detector-meters";
import {
  Badge,
  Breadcrumbs,
  DataTable,
  EmptyState,
  PageHeader,
  SkeletonTable,
  catalogStatusVariant,
} from "@/components/ui";

type CatalogDetectorMeter = components["schemas"]["CatalogDetectorMeter"];

export const Route = createFileRoute("/_authenticated/catalog/detector-meters")({
  component: CatalogDetectorMetersPage,
});

const columnHelper = createColumnHelper<CatalogDetectorMeter>();

function CatalogDetectorMetersPage() {
  const { t } = useTranslation(["catalog", "common", "nav"]);

  // Lookup table is ~29 rows — fetch the full list in one page. Sort +
  // filter run client-side.
  const { data, isLoading, isError, error } = useCatalogDetectorMeters({ limit: 200 });
  const items = useMemo(() => data?.items ?? [], [data]);

  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "code", desc: false }]);

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (it) =>
        it.code.toLowerCase().includes(needle) || it.description.toLowerCase().includes(needle),
    );
  }, [items, search]);

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("code", {
          id: "code",
          enableSorting: true,
          header: () => t("lookup.fields.code"),
          cell: (ctx) => <span className="font-mono font-medium">{ctx.getValue()}</span>,
        }),
        columnHelper.accessor("description", {
          id: "description",
          enableSorting: true,
          header: () => t("lookup.fields.description"),
          cell: (ctx) => ctx.getValue(),
        }),
        columnHelper.accessor("status", {
          id: "status",
          enableSorting: true,
          header: () => t("filters.status"),
          cell: (ctx) => {
            const s = ctx.getValue();
            return <Badge variant={catalogStatusVariant(s)}>{t(`status.${s}`)}</Badge>;
          },
        }),
        // `columnHelper.accessor` returns `ColumnDef<TData, TValue>` where
        // `TValue` is narrowed per-column. An array of heterogeneous
        // accessor results does not unify to `ColumnDef<TData, unknown>[]`
        // via inference, so we widen here. Canonical TanStack Table v8.
      ] as ColumnDef<CatalogDetectorMeter, unknown>[],
    [t],
  );

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          { label: t("catalog.group", { ns: "nav" }) },
          { label: t("page.detectorMetersTitle") },
        ]}
      />
      <PageHeader title={t("page.detectorMetersTitle")} />

      <div className="mt-6">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("filters.searchDetectorMeters")}
          className="w-full max-w-sm rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-700 dark:bg-gray-700 dark:text-white"
        />
      </div>

      {isError && (
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">
          {error instanceof Error ? error.message : t("errors.loadFailed")}
        </div>
      )}

      {isLoading ? (
        <div className="mt-6">
          <SkeletonTable columns={3} rows={5} />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title={t("page.detectorMetersTitle")}
            description={t("empty.detectorMeters")}
          />
        </div>
      ) : (
        <div className="mt-6">
          <DataTable
            columns={columns}
            data={filteredItems}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={false}
          />
        </div>
      )}
    </div>
  );
}
